import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Error } from "@narmi/design_system";
import { useNotificationContext } from "../NotificationContext";

const UploadStatusIcon = ({ isUploading }) => (
  <div className="status-icon-circle-container">
    <div className="narmi-icon-solid-circle" />
    <div className="status-icon-wrapper">
      <div className={isUploading ? "narmi-icon-refresh-cw" : "narmi-icon-check"} />
    </div>
  </div>
);
UploadStatusIcon.propTypes = {
  isUploading: PropTypes.bool,
};

const FileUploader = ({
  sendRequest,
  onChange,
  value,
  error,
  fileKey,
  expand = false,
  onRemove,
  maxFileUploadSizeBytes,
  shouldSuppressErrorNotification,
  messages = {
    errorNoFile: "No file detected. Please try again",
    errorMultipleUnsupported: "Multiple file uploads unsupported. Please try again",
    errorUnsupportedType:
      "Unfortunately, we don't support this file type. Please upload a supported file type such as .jpg, .pdf, .csv, .ppt, .doc, etc.",
    errorFailedExceedsSize: `Your file exceeds the ${maxFileUploadSizeBytes ? (maxFileUploadSizeBytes / (1024 * 1024)).toFixed(1) : 2.5}MB maximum size. Please upload a smaller file.`,
    errorFailed: "Upload failed. Please try again or reach out to Support for assistance.",
    label: "Drag and drop here or click to upload a file",
  },
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  /* when uploadedFileName doesn't have a value, the file-uploader view is displayed.
     otherwise, the uploaded-file view is displayed. */
  const [uploadedFileName, setUploadedFileName] = useState(value || null);
  const [isUploading, setIsUploading] = useState(false);
  const nativeInputRef = useRef(null);

  const { sendNotification } = useNotificationContext();

  const validateFiles = (files) => {
    if (!files || files.length === 0) {
      sendNotification({
        type: "negative",
        text: messages.errorNoFile,
      });
      return false;
    }
    if (files.length > 1) {
      sendNotification({
        type: "negative",
        text: messages.errorMultipleUnsupported,
      });
      return false;
    }
    return true;
  };

  const onUpload = async (file) => {
    if (maxFileUploadSizeBytes && file.size > maxFileUploadSizeBytes) {
      sendNotification({
        type: "negative",
        text: messages.errorFailedExceedsSize,
      });
      return;
    }
    setUploadedFileName(file.name);
    setIsUploading(true);
    try {
      /* do the api call */
      await sendRequest(file);
      onChange(file.name);
    } catch (error) {
      const errorString = "Inbound file is not allowed!";
      if (shouldSuppressErrorNotification?.(error)) {
        // Parent is already surfacing the error inline.
      } else if (
        typeof error === "string" &&
        error.toLowerCase().includes(errorString.toLowerCase())
      ) {
        sendNotification({
          type: "negative",
          text: messages.errorUnsupportedType,
        });
      } else {
        sendNotification({
          type: "negative",
          text: messages.errorFailed,
        });
      }
      setUploadedFileName("");
    } finally {
      setIsUploading(false);
    }
  };

  // handle drag events
  const handleDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (["dragenter", "dragover"].includes(e.type)) {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  // triggers when file is dragged and dropped
  const handleDropUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (validateFiles(e.dataTransfer.files)) {
      onUpload(e.dataTransfer.files[0]);
    }
  };

  // triggers when file is selected with click
  const handleClickUpload = (e) => {
    e.preventDefault();

    if (validateFiles(e.target.files)) {
      onUpload(e.target.files[0]);
    }
  };

  const softRemoveUploadedFile = () => {
    if (onRemove) {
      onRemove();
    } else {
      setUploadedFileName(null);
      onChange(null);
    }
  };

  // triggers the native input when the 'click to upload file' button is clicked
  const triggerNativeInput = () => {
    nativeInputRef.current.click();
  };

  /* the file-uploader view */
  const noUploadedFileState = (
    <>
      <input
        ref={nativeInputRef}
        type="file"
        style={{ display: "none" }}
        id={`native-input-file-${fileKey}`}
        onChange={handleClickUpload}
      />
      <label
        id="file-uploader-label"
        htmlFor={`native-input-file-${fileKey}`}
        className={isDragActive ? "drag-active" : ""}
      >
        <div className="narmi-icon-upload" style={{ paddingRight: "var(--space-s)" }} />
        <div>
          <button type="button" className="upload-button" onClick={triggerNativeInput}>
            <span style={{ color: "var(--font-color-heading)" }}>{messages.label}</span>
            {messages.maxSizeLabel && (
              <span className="fontColor--secondary">&nbsp;{messages.maxSizeLabel}</span>
            )}
          </button>
        </div>
      </label>
    </>
  );

  /* the "uploaded-file" view*/
  const hasUploadedFileState = (
    // can replace the uploaded file by dragging a new file into the box
    <div id="uploaded-file-box" className={`${isDragActive ? "drag-active" : ""}`}>
      <UploadStatusIcon isUploading={isUploading} />
      <div className="uploaded-file-name">{uploadedFileName}</div>
      <div className="x-button">
        <Button kind="plain" type="button" startIcon="x" onClick={softRemoveUploadedFile} />
      </div>
    </div>
  );

  return (
    <>
      <div
        id="file-uploader-wrapper"
        className={error ? "has-error" : ""}
        style={{ maxWidth: expand ? "100%" : null }}
        onDragEnter={handleDragEvent}
      >
        {uploadedFileName ? hasUploadedFileState : noUploadedFileState}
        {isDragActive && (
          <div
            id="drag-file-element"
            onDragEnter={handleDragEvent}
            onDragLeave={handleDragEvent}
            onDragOver={handleDragEvent}
            onDrop={handleDropUpload}
          />
        )}
      </div>
      <Error error={error} marginTop="none" />
    </>
  );
};
FileUploader.propTypes = {
  sendRequest: PropTypes.func,
  onChange: PropTypes.func,
  error: PropTypes.string,
  fileKey: PropTypes.string,
  expand: PropTypes.bool,
  onRemove: PropTypes.func,
  messages: PropTypes.object,
  maxFileUploadSizeBytes: PropTypes.number,
  shouldSuppressErrorNotification: PropTypes.func,
};

export default FileUploader;
